jQuery($ => {
    class LocationFinder {
      constructor(element) {
        this._        = element;
        this._element = $(element);
        this.mapElement = null;
        this.map = null;
        this.MapConstructor = null;
        this.MarkerConstructor = null;
        this.usingAdvancedMarkers = false;
        this.markers = [];
        this.markersByLocationId = {};
        this.locations = [];
        this.markerIconUrl = null;
        this.markerIconDataUrl = null;
        this.markerIconActiveDataUrl = null;
        this.activeLocationDomId = null;
        this.searchRequestId = 0;
  
        this.init();
      }
  
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Initialize
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
  
      init() {
        this.loadLocations();
        this.setupHandlers();
        Promise.resolve(this.initializeMap())
          .finally(() => {
            const initialSearchQuery = this.getInitialSearchQuery();

            if (initialSearchQuery) {
              this.searchUsingInitialQuery(initialSearchQuery);
              return;
            }

            this.promptForCurrentLocationSearch();
          });
        this._element.trigger( 'init', this );
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Load Locations
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      loadLocations() {
        const localizedFinderData = window.LOCATION_FINDER || {};

        this.locations = Array.isArray(localizedFinderData.locations)
          ? localizedFinderData.locations
          : [];
        this.markerIconUrl = localizedFinderData.marker_icon || null;
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Initialize Map
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      async initializeMap() {
        const mapContainer = this._element.find('.o-location-finder__map')[0];
        this.mapElement = mapContainer;
        
        if (!mapContainer) {
          console.warn('Map element not found');
          return;
        }

        try {
          await this.loadGoogleMapsClasses();
        } catch (error) {
          console.error('Google Maps failed to load:', error);
          return;
        }

        // Calculate center point from all locations
        const center = this.calculateCenter();

        // Initialize map
        this.map = new this.MapConstructor(mapContainer, {
          zoom: 12,
          center: center,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: true,
          styles: [
            {
              featureType: 'poi',
              stylers: [{ visibility: 'off' }],
            },
          ],
        });

        await this.prepareMarkerIcon();

        const locationsWithCoordinates = this.getLocationsWithCoordinates();
        if (locationsWithCoordinates.length > 0) {
          this.addMarkers(locationsWithCoordinates);
          this.fitMapToMarkers();
        }
      }

      async loadGoogleMapsClasses() {
        if (this.MapConstructor && this.MarkerConstructor) {
          return;
        }

        if (!window.google || !window.google.maps) {
          throw new Error('Google Maps API is not available on window.google.maps.');
        }

        // New loader API: google.maps.importLibrary
        if (typeof window.google.maps.importLibrary === 'function') {
          const { Map } = await window.google.maps.importLibrary('maps');
          let markerLibrary = {};

          try {
            markerLibrary = await window.google.maps.importLibrary('marker');
          } catch (error) {
            console.warn('Google Maps marker library failed to load. Attempting fallback marker constructors.', error);
          }

          this.MapConstructor = Map;

          if (window.google.maps.Marker) {
            this.MarkerConstructor = window.google.maps.Marker;
            this.usingAdvancedMarkers = false;
            return;
          }

          if (markerLibrary && markerLibrary.AdvancedMarkerElement) {
            this.MarkerConstructor = markerLibrary.AdvancedMarkerElement;
            this.usingAdvancedMarkers = true;
            return;
          }

          this.MarkerConstructor = null;
          return;
        }

        // Legacy loader API: global constructors
        this.MapConstructor = window.google.maps.Map;
        this.MarkerConstructor = window.google.maps.Marker;
        this.usingAdvancedMarkers = false;
      }

      async prepareMarkerIcon() {
        if (!this.markerIconUrl) {
          return;
        }

        try {
          const response = await fetch(this.markerIconUrl, { credentials: 'same-origin' });
          if (!response.ok) {
            throw new Error(`Marker icon request failed with status ${response.status}`);
          }

          const svgSource = await response.text();
          this.markerIconDataUrl = this.buildMarkerIconDataUrl(svgSource, this.getMarkerColor());
          this.markerIconActiveDataUrl = this.buildMarkerIconDataUrl(svgSource, this.getActiveMarkerColor());
        } catch (error) {
          console.warn('Unable to prepare custom marker icon. Falling back to default marker.', error);
          this.markerIconDataUrl = null;
          this.markerIconActiveDataUrl = null;
        }
      }

      buildMarkerIconDataUrl(svgSource, markerColor) {
        const colorizedSvg = svgSource.replace(/currentColor/g, markerColor);
        return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(colorizedSvg)}`;
      }

      getMarkerColor() {
        const mapElement = this.mapElement || this._element[0];
        const styles = window.getComputedStyle(mapElement);
        const cssVarColor = styles.getPropertyValue('--marker-icon-color').trim()
          || styles.getPropertyValue('--marker-color').trim();

        if (cssVarColor) {
          return cssVarColor;
        }

        return styles.color || '#0f766e';
      }

      getMarkerSize() {
        const mapElement = this.mapElement || this._element[0];
        const styles = window.getComputedStyle(mapElement);
        const cssVarSize = styles.getPropertyValue('--marker-size').trim();
        const parsedSize = parseFloat(cssVarSize);

        if (Number.isFinite(parsedSize) && parsedSize > 0) {
          return parsedSize;
        }

        return 28;
      }

      getActiveMarkerSize() {
        const mapElement = this.mapElement || this._element[0];
        const styles = window.getComputedStyle(mapElement);
        const cssVarSize = styles.getPropertyValue('--marker-size-active').trim();
        const parsedSize = parseFloat(cssVarSize);

        if (Number.isFinite(parsedSize) && parsedSize > 0) {
          return parsedSize;
        }

        return this.getMarkerSize() * 1.25;
      }

      getActiveMarkerColor() {
        const mapElement = this.mapElement || this._element[0];
        const styles = window.getComputedStyle(mapElement);
        const cssVarColor = styles.getPropertyValue('--marker-icon-color-hover').trim()
          || styles.getPropertyValue('--marker-color-hover').trim();

        if (cssVarColor) {
          return cssVarColor;
        }

        return styles.getPropertyValue('--palette-two--secondary').trim() || '#16a34a';
      }

      getMarkerIconOptions(iconUrl, markerSize = this.getMarkerSize()) {
        if (!iconUrl || !window.google || !window.google.maps) {
          return null;
        }

        return {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(markerSize, markerSize),
          anchor: new window.google.maps.Point(markerSize / 2, markerSize),
          labelOrigin: new window.google.maps.Point(markerSize / 2, -14),
        };
      }

      getMarkerLabelClassName(isActive = false) {
        return isActive
          ? 'a-tag a-tag--highlighted o-location-finder__marker-label'
          : 'a-tag o-location-finder__marker-label';
      }

      getMarkerLabelOptions(locationName, isActive = false) {
        const trimmedName = typeof locationName === 'string' ? locationName.trim() : '';

        return trimmedName
          ? {
            text: trimmedName,
            className: this.getMarkerLabelClassName(isActive),
            color: this.getMarkerTextColor(isActive),
          }
          : null;
      }

      getMarkerTextColor(isActive = false) {
        const mapElement = this.mapElement || this._element[0];
        const styles = window.getComputedStyle(mapElement);
        const variableName = isActive ? '--marker-text-color-hover' : '--marker-text-color';
        const fallbackVariableName = isActive ? '--marker-text-color' : '--text--base-color';

        return styles.getPropertyValue(variableName).trim()
          || styles.getPropertyValue(fallbackVariableName).trim()
          || '#fff';
      }

      createAdvancedMarkerContent(locationName, iconUrl, markerSize, isActive = false) {
        const container = document.createElement('div');
        container.className = 'o-location-finder__marker-content';

        if (typeof locationName === 'string' && locationName.trim()) {
          const markerLabel = document.createElement('span');
          markerLabel.textContent = locationName.trim();
          markerLabel.dataset.role = 'marker-label';
          markerLabel.className = this.getMarkerLabelClassName(isActive);
          container.appendChild(markerLabel);
        }

        const markerImage = document.createElement('img');
        markerImage.src = iconUrl;
        markerImage.alt = locationName || 'Location marker';
        markerImage.dataset.role = 'marker-icon';
        markerImage.style.width = `${markerSize}px`;
        markerImage.style.height = `${markerSize}px`;
        container.appendChild(markerImage);

        return container;
      }

      updateAdvancedMarkerVisual(marker, iconUrl, markerSize) {
        if (!marker || !iconUrl || !marker.content) {
          return;
        }

        const markerImage = marker.content.querySelector('[data-role="marker-icon"]');
        if (!markerImage) {
          return;
        }

        markerImage.src = iconUrl;
        markerImage.style.width = `${markerSize}px`;
        markerImage.style.height = `${markerSize}px`;
      }

      updateAdvancedMarkerLabel(marker, isActive) {
        if (!marker || !marker.content) {
          return;
        }

        const markerLabel = marker.content.querySelector('[data-role="marker-label"]');
        if (!markerLabel) {
          return;
        }

        markerLabel.className = this.getMarkerLabelClassName(isActive);
      }

      setActiveMarkerByLocationId(locationDomId) {
        this.activeLocationDomId = locationDomId;

        this.markers.forEach((marker) => {
          const isActiveMarker = marker.locationDomId === this.activeLocationDomId;

          if (this.usingAdvancedMarkers) {
            this.updateAdvancedMarkerLabel(marker, isActiveMarker);

            if (!this.markerIconDataUrl) {
              return;
            }

            const iconUrl = isActiveMarker && this.markerIconActiveDataUrl
              ? this.markerIconActiveDataUrl
              : this.markerIconDataUrl;
            const markerSize = isActiveMarker ? this.getActiveMarkerSize() : this.getMarkerSize();
            this.updateAdvancedMarkerVisual(marker, iconUrl, markerSize);
            return;
          }

          if (typeof marker.setLabel === 'function') {
            const label = this.getMarkerLabelOptions(marker.locationName, isActiveMarker);
            if (label) {
              marker.setLabel(label);
            }
          }

          if (!this.markerIconDataUrl) {
            return;
          }

          if (typeof marker.setIcon !== 'function') {
            return;
          }

          const iconUrl = isActiveMarker && this.markerIconActiveDataUrl
            ? this.markerIconActiveDataUrl
            : this.markerIconDataUrl;
          const markerSize = isActiveMarker ? this.getActiveMarkerSize() : this.getMarkerSize();

          const icon = this.getMarkerIconOptions(iconUrl, markerSize);

          if (icon) {
            marker.setIcon(icon);
          }
        });
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Calculate Center
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      calculateCenter() {
        const locationsWithCoordinates = this.getLocationsWithCoordinates();

        if (locationsWithCoordinates.length === 0) {
          return { lat: 37.0902, lng: -95.7129 }; // Center of USA
        }

        return {
          lat: locationsWithCoordinates[0].lat,
          lng: locationsWithCoordinates[0].lng,
        };
      }

      getLocationsWithCoordinates() {
        return this.locations
          .map((location) => ({
            ...location,
            lat: parseFloat(location.lat),
            lng: parseFloat(location.lng),
          }))
          .filter((location) => Number.isFinite(location.lat) && Number.isFinite(location.lng));
      }

      getLocationDomId(location) {
        if (location.dom_id) {
          return location.dom_id;
        }

        if (location.slug) {
          return `${location.slug}-location`;
        }

        if (location.id) {
          return `${location.id}-location`;
        }

        return null;
      }

      getSearchPill() {
        return this._element.find('.o-location-finder__pill');
      }

      getSearchDisplayText(searchMeta) {
        if (typeof searchMeta === 'string') {
          return searchMeta.trim();
        }

        if (!searchMeta || typeof searchMeta !== 'object') {
          return '';
        }

        const preferredKeys = [
          'zip',
          'postal_code',
          'postalCode',
          'zip_code',
          'query',
          'search',
          'address',
          'formatted_address',
          'formattedAddress',
        ];

        for (const key of preferredKeys) {
          const value = searchMeta[key];

          if (typeof value === 'string' && value.trim()) {
            return value.trim();
          }
        }

        const city = typeof searchMeta.city === 'string' ? searchMeta.city.trim() : '';
        const state = typeof searchMeta.state === 'string' ? searchMeta.state.trim() : '';

        if (city && state) {
          return `${city}, ${state}`;
        }

        if (city) {
          return city;
        }

        if (searchMeta.current_location) {
          return 'your current location';
        }

        return '';
      }

      getSearchActionInput() {
        return this._element.find('#location-finder-map-search-button').closest('.m-action-input');
      }

      getSearchRadius() {
        return 25;
      }

      getInitialSearchQuery() {
        const params = new window.URLSearchParams(window.location.search);
        const nearQuery = params.get('near');

        if (typeof nearQuery !== 'string') {
          return '';
        }

        return nearQuery.trim();
      }

      async searchUsingInitialQuery(initialSearchQuery) {
        const addressInput = this._element.find('#location-finder-map-search-input');
        addressInput.val(initialSearchQuery);

        await this.runSearchRequest({
          address: initialSearchQuery,
          radius: this.getSearchRadius(),
        }, {
          address: initialSearchQuery,
          radius: this.getSearchRadius(),
        });
      }

      promptForCurrentLocationSearch() {
        if (!window.navigator.geolocation) {
          return;
        }

        this.searchUsingCurrentLocation();
      }

      async runSearchRequest(searchPayload, fallbackSearchMeta) {
        const requestId = ++this.searchRequestId;
        const actionInput = this.getSearchActionInput();
        actionInput.addClass('m-action-input--loading');

        try {
          const response = await fetch('/wp-json/custom/v1/locations/search', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(searchPayload),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Search failed with status ${response.status}`);
          }

          const result = await response.json();
          console.log('Location search results:', result);

          if (requestId !== this.searchRequestId) {
            return;
          }

          if (result.success && Array.isArray(result.data)) {
            this.updateLocationsFromSearchResults(result.data, result.search || fallbackSearchMeta);
          } else {
            console.warn('No locations found for the provided search');
          }
        } catch (error) {
          console.error('Location search error:', error);
        } finally {
          actionInput.removeClass('m-action-input--loading');
        }
      }

      searchUsingCurrentLocation() {
        if (!window.navigator.geolocation) {
          return;
        }

        const actionInput = this.getSearchActionInput();
        actionInput.addClass('m-action-input--loading');

        window.navigator.geolocation.getCurrentPosition(
          (position) => {
            actionInput.removeClass('m-action-input--loading');

            this.runSearchRequest(
              {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                radius: this.getSearchRadius(),
              },
              {
                current_location: true,
                radius: this.getSearchRadius(),
              }
            );
          },
          (error) => {
            actionInput.removeClass('m-action-input--loading');
            console.warn('Unable to access current location.', error);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000,
          }
        );
      }

      updateSearchPill(resultCount, searchQuery) {
        const pill = this.getSearchPill();
        const queryText = this.getSearchDisplayText(searchQuery);
        const normalizedCount = Number.isFinite(resultCount) ? resultCount : 0;
        const locationLabel = normalizedCount === 1 ? 'location' : 'locations';
        const searchRadius = searchQuery && Number.isFinite(Number(searchQuery.radius))
          ? Number(searchQuery.radius)
          : this.getSearchRadius();
        const fallbackLocation = queryText || 'your search';
        const text = normalizedCount === 0
          ? `No locations found within ${searchRadius} miles of ${fallbackLocation}`
          : queryText
            ? `Showing ${normalizedCount} ${locationLabel} near ${queryText}`
            : `Showing ${normalizedCount} ${locationLabel}`;

        pill.find('.a-pill__text').text(text);
        pill.addClass('o-location-finder__pill--visible');
      }

      hideSearchPill() {
        this.getSearchPill().removeClass('o-location-finder__pill--visible');
      }

      resetSearchResults() {
        this.searchRequestId += 1;
        const hiddenClass = 'o-location-finder__locations-list-item--hidden';
        const activeClass = 'o-location-finder__locations-list-item--active';
        const mobileTopClass = 'o-location-finder__locations-list-item--mobile-top';
        this._element.find('.o-location-finder__locations-list-item--search-result').remove();
        this._element.find('.o-location-finder__locations-list-item--initial').removeClass(hiddenClass);
        this._element.find('.o-location-finder__locations-list-item').removeClass(activeClass);
        this._element.find('.o-location-finder__locations-list-item').removeClass(mobileTopClass);
        this._element.find('#location-finder-map-search-input').val('');

        const allLocations = this.getLocationsWithCoordinates();
        if (allLocations.length > 0) {
          this.addMarkers(allLocations);
          this.fitMapToMarkers();
        }

        this.setActiveMarkerByLocationId(null);
        this.hideSearchPill();
      }

      activateLocationListItemById(locationDomId) {
        if (!locationDomId) {
          return;
        }

        const activeClass = 'o-location-finder__locations-list-item--active';
        const listItems = this._element.find('.o-location-finder__locations-list-item');
        const targetItem = this._element.find(`#${locationDomId}`);

        listItems.removeClass(activeClass);

        if (!targetItem.length) {
          return;
        }

        targetItem.addClass(activeClass);
        this.setActiveMarkerByLocationId(locationDomId);

        if (this.isMobileViewport()) {
          const mobileTopClass = 'o-location-finder__locations-list-item--mobile-top';
          const list = this._element.find('.o-location-finder__locations-list');

          this._element.find('.o-location-finder__locations-list-item').removeClass(mobileTopClass);
          targetItem.addClass(mobileTopClass);

          if (list.length) {
            list.prepend(targetItem);
          }

          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });

          return;
        }

        const targetElement = targetItem[0];
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }

      centerMapOnLocationById(locationDomId) {
        if (!this.map || !locationDomId) {
          return;
        }

        const marker = this.markersByLocationId[locationDomId];
        if (!marker) {
          return;
        }

        const markerPosition = this.getMarkerPosition(marker);
        if (!markerPosition) {
          return;
        }

        this.map.panTo(markerPosition);
        this.map.setZoom(14);
      }

      fitMapToMarkers() {
        if (!this.map || this.markers.length === 0 || !window.google || !window.google.maps) {
          return;
        }

        if (this.markers.length === 1) {
          const markerPosition = this.getMarkerPosition(this.markers[0]);
          if (!markerPosition) {
            return;
          }

          this.map.setCenter(markerPosition);
          this.map.setZoom(14);
          return;
        }

        const bounds = new window.google.maps.LatLngBounds();
        this.markers.forEach((marker) => {
          const markerPosition = this.getMarkerPosition(marker);
          if (markerPosition) {
            bounds.extend(markerPosition);
          }
        });
        this.map.fitBounds(bounds);
      }

      getMarkerPosition(marker) {
        if (!marker) {
          return null;
        }

        if (typeof marker.getPosition === 'function') {
          return marker.getPosition();
        }

        return marker.position || null;
      }

      isMobileViewport() {
        return window.matchMedia('(max-width: 599px)').matches;
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Add Markers
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      addMarkers(geocodedLocations) {
        if (!this.map) {
          console.warn('Map not initialized');
          return;
        }

        if (!this.MarkerConstructor) {
          console.error('Google Maps marker constructor is not available.');
          return;
        }

        // Clear existing markers
        this.markers.forEach((marker) => {
          if (typeof marker.setMap === 'function') {
            marker.setMap(null);
          } else {
            marker.map = null;
          }
        });
        this.markers = [];
        this.markersByLocationId = {};

        // Add new markers
        geocodedLocations.forEach((location) => {
          const Marker = this.MarkerConstructor || window.google.maps.Marker;
          const locationDomId = this.getLocationDomId(location);

          const markerOptions = {
            position: { lat: location.lat, lng: location.lng },
            map: this.map,
            title: location.name,
          };

          const markerLabel = this.getMarkerLabelOptions(location.name);
          if (markerLabel) {
            markerOptions.label = markerLabel;
          }

          if (this.markerIconDataUrl && !this.usingAdvancedMarkers) {
            markerOptions.icon = this.getMarkerIconOptions(this.markerIconDataUrl);
          }

          if (this.markerIconDataUrl && this.usingAdvancedMarkers) {
            const markerSize = this.getMarkerSize();
            markerOptions.content = this.createAdvancedMarkerContent(location.name, this.markerIconDataUrl, markerSize);
            delete markerOptions.label;
          }

          const marker = new Marker(markerOptions);

          marker.locationDomId = locationDomId;
          marker.locationName = location.name || '';
          marker.addListener('click', () => {
            this.centerMapOnLocationById(marker.locationDomId);
            this.activateLocationListItemById(marker.locationDomId);
          });

          this.markers.push(marker);

          if (locationDomId) {
            this.markersByLocationId[locationDomId] = marker;
          }
        });
      }
  
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Setup Handlers
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
  
      setupHandlers() {
        this._element.on('click', '.o-location-finder__locations-list-item', (event) => {
          if ($(event.target).closest('a, button, input, select, textarea, label').length) {
            return;
          }

          const locationDomId = event.currentTarget.id;
          this.activateLocationListItemById(locationDomId);
          this.centerMapOnLocationById(locationDomId);
        });

        // Search button handler
        this._element.on('click', '#location-finder-map-search-button', (event) => {
          event.preventDefault();
          this.handleSearchSubmit();
        });

        // Search input enter key handler
        this._element.on('keypress', '#location-finder-map-search-input', (event) => {
          if (event.which === 13) {
            event.preventDefault();
            this.handleSearchSubmit();
          }
        });

        this._element.on('input', '#location-finder-map-search-input', (event) => {
          if (!$(event.currentTarget).val().trim()) {
            this.resetSearchResults();
          }
        });

        this._element.on('click', '.o-location-finder__pill .a-pill__close-btn', (event) => {
          event.preventDefault();
          this.resetSearchResults();
        });
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Search Handler
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      async handleSearchSubmit() {
        const addressInput = this._element.find('#location-finder-map-search-input');
        const address = addressInput.val().trim();

        if (!address) {
          console.warn('Please enter an address');
          return;
        }

        await this.runSearchRequest({
          address,
          radius: this.getSearchRadius(),
        }, {
          address,
          radius: this.getSearchRadius(),
        });
      }

      updateLocationsFromSearchResults(searchResults, searchMeta) {
        if (!Array.isArray(searchResults)) {
          console.warn('No results to update');
          return;
        }

        const hiddenClass = 'o-location-finder__locations-list-item--hidden';
        const list = this._element.find('.o-location-finder__locations-list');

        list.find('.o-location-finder__locations-list-item--search-result').remove();

        if (searchResults.length === 0) {
          list.find('.o-location-finder__locations-list-item--initial').removeClass(hiddenClass);

          const allLocations = this.getLocationsWithCoordinates();
          this.addMarkers(allLocations);
          this.fitMapToMarkers();
          this.setActiveMarkerByLocationId(null);
          this.updateSearchPill(0, searchMeta);
          return;
        }

        list.find('.o-location-finder__locations-list-item--initial').addClass(hiddenClass);

        searchResults.forEach((location) => {
          if (typeof location.location_card_html !== 'string' || !location.location_card_html.trim()) {
            return;
          }

          const baseDomId = this.getLocationDomId(location);
          location.dom_id = baseDomId ? `search-result-${baseDomId}` : null;

          $('<li>', {
            class: 'o-location-finder__locations-list-item o-location-finder__locations-list-item--search-result',
            id: location.dom_id || undefined,
          }).html(location.location_card_html).appendTo(list);
        });

        this.setActiveMarkerByLocationId(null);
        this.updateSearchPill(searchResults.length, searchMeta);

        // Update markers to only show search results
        const locationsToShow = searchResults.map(location => ({
          ...location,
          lat: parseFloat(location.lat),
          lng: parseFloat(location.lng),
        })).filter(location => Number.isFinite(location.lat) && Number.isFinite(location.lng));

        this.addMarkers(locationsToShow);
        this.fitMapToMarkers();

        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }


      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Public Methods
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      getLocations() {
        return this.locations;
      }

      getMap() {
        return this.map;
      }
    }
  
    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
    // ✅ Setup jQuery Plugin
    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
  
    $.fn.location_finder = function() {
      var _ = this,
          opt = arguments[0],
          args = Array.prototype.slice.call(arguments, 1),
          l = _.length,
          i,
          ret;
      for (i = 0; i < l; i++) {
        if (typeof opt == 'object' || typeof opt == 'undefined')
          _[i].location_finder = new LocationFinder(_[i], opt);
        else
          ret = _[i].location_finder[opt].apply(_[i].location_finder, args);
        if (typeof ret != 'undefined') return ret;
      }
      return _;
    }
  
    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
    // ✅ Run
    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
  
    $('.o-location-finder').location_finder();
});
